import { isNil } from 'lodash';
import { Storage } from './Storage';

const TTL_MAP_KEY = 'ttl-value-map';

interface TimeToLiveRecord {
  expiresAt: number;
}

interface TimeToLiveMap {
  [key: string]: TimeToLiveRecord;
}

export namespace JsonStorage {
  /**
   * Set a value to @MBStorage using a time to live caching strategy
   *
   * @param key the storage key associated with the data
   * @param value the value to store
   * @param timeToLiveMs the time for the value to be valid in the storage
   */
  export function set<T>(key: string, value: T, timeToLiveMs?: number): void {
    Storage.setJSON<T>(key, value);
    if (isNil(timeToLiveMs)) {
      return;
    }
    const timeNow = Date.now();
    const timeToLiveMap: TimeToLiveMap = getTimeToLiveMap();
    const newTimeToLiveMap = {
      ...timeToLiveMap,
      [key]: { expiresAt: timeNow + timeToLiveMs },
    };
    Storage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, newTimeToLiveMap);
  }

  /**
   * Get a value from @MBStorage that was previously set using @set
   *
   * @param key the storage key associated with the data
   * @returns the stored data if it exists within the time to live, undefined if
   * it does not exist or has expired
   */
  export function get<T>(key: string): T | undefined {
    const timeToLiveMap: TimeToLiveMap = getTimeToLiveMap();
    const timeToLiveRecord = timeToLiveMap[key];
    const isExpired = isRecordExpired(timeToLiveRecord);
    if (isExpired) {
      evict(key);
      return undefined;
    } else {
      return Storage.getJSON<T>(key);
    }
  }

  /**
   * Clear a value from the time to live storage
   *
   * @param key the storage key associated with the data
   */
  export function remove(key: string): void {
    forgetTimeToLive(key);
    Storage.remove(key);
  }

  /**
   * Cleanup all stale values from the time to live storage
   */
  export function cleanup(): void {
    removeExpiredKeys();
    removeUnusedTTLKeys();
  }

  export function removeExpiredKeys(): void {
    const timeToLiveMap: TimeToLiveMap = getTimeToLiveMap();
    Object.keys(timeToLiveMap)
      .filter(key => isRecordExpired(timeToLiveMap[key]))
      .forEach(expiredKey => {
        delete timeToLiveMap[expiredKey];
        Storage.remove(expiredKey);
      });

    Storage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, timeToLiveMap);
  }

  export function removeUnusedTTLKeys(): void {
    const keys = Storage.keys();
    const timeToLiveMap: TimeToLiveMap = getTimeToLiveMap();
    Object.keys(timeToLiveMap)
      .filter(key => !keys.includes(key))
      .forEach(unusedKey => {
        delete timeToLiveMap[unusedKey];
      });

    Storage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, timeToLiveMap);
  }
}

/**
 * Drop an expired key without broadcasting the change, which is unsafe from the
 * render phase `get` is called in.
 */
function evict(key: string): void {
  forgetTimeToLive(key);
  Storage.removeWithoutBroadcast(key);
}

function forgetTimeToLive(key: string): void {
  const timeToLiveMap: TimeToLiveMap = getTimeToLiveMap();
  delete timeToLiveMap[key];
  Storage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, timeToLiveMap);
}

function getTimeToLiveMap(): TimeToLiveMap {
  const timeToLiveMap = Storage.getJSON<TimeToLiveMap>(TTL_MAP_KEY);
  return timeToLiveMap ?? {};
}

function isRecordExpired(record?: TimeToLiveRecord): boolean {
  if (isNil(record?.expiresAt)) {
    return false;
  }
  const timeNow = Date.now();
  return timeNow > record.expiresAt;
}
